import type { Appointment, AppointmentReference, AppointmentStatus } from '@/types/appointment'
import type { AppointmentGroupMember } from '@/types/appointmentGroup'

/**
 * Cita de la API (snake_case, camelCase o mezcla) → `Appointment`; extraído tal cual de AppointmentsService.js.
 * Los `as` son la frontera con la API: el JSON no se valida en runtime. AppointmentsService.spec.ts congela la salida.
 */

// Cita cruda del backend: cualquier llave puede faltar o venir en otro casing.
type Raw = Record<string, unknown>

const ALLOWED_STATUS = new Set<AppointmentStatus>(['pending', 'confirmed', 'cancelled', 'completed', 'no_show'])

const asRaw = (value: unknown): Raw => (value && typeof value === 'object' ? (value as Raw) : {})

/** Estado desde la API (camelCase, snake_case o legacy); por defecto 'pending'. */
export function normalizeStatusFromApi(apt: unknown): AppointmentStatus {
  if (!apt || typeof apt !== 'object') return 'pending'
  const raw = asRaw(apt)
  const value = raw.status ?? raw.appointment_status ?? raw.appointmentStatus ?? raw.state ?? null
  if (value == null || value === '') return 'pending'
  const s = String(value).trim().toLowerCase().replace(/\s+/g, '_')
  if (ALLOWED_STATUS.has(s as AppointmentStatus)) return s as AppointmentStatus
  if (s === 'canceled') return 'cancelled'
  if (s === 'complete') return 'completed'
  return 'pending'
}

/** HH:mm de fin; sin duración válida asume 60 minutos. */
export function calculateEndTime(startTime: string, durationMinutes: unknown): string {
  if (!startTime) return ''
  const [hours, minutes] = String(startTime).split(':').map(Number)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return ''
  const total = (hours as number) * 60 + (minutes as number) + (Number(durationMinutes) || 60)
  const endHours = Math.floor(total / 60) % 24
  const endMinutes = total % 60
  return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`
}

/** Hermana de una cita agrupada (group_members o `group` de la respuesta de POST). */
export function normalizeGroupMember(member: unknown): AppointmentGroupMember {
  const m = asRaw(member)
  return {
    // Cada hermana es una fila de appointments: el backend siempre manda su id.
    id: m.id as AppointmentGroupMember['id'],
    clientId: (m.client_id ?? m.clientId ?? null) as AppointmentGroupMember['clientId'],
    clientName: (m.client_name ?? m.clientName ?? 'Sin nombre') as string,
    workerId: (m.worker_id ?? m.workerId ?? null) as AppointmentGroupMember['workerId'],
    workerName: (m.worker_name ?? m.workerName ?? '') as string,
    status: normalizeStatusFromApi(m),
    appointmentDate: String(m.appointment_date ?? m.appointmentDate ?? '').split('T')[0] as string,
    appointmentTime: String(m.appointment_time ?? m.appointmentTime ?? '').slice(0, 5)
  }
}

function normalizeGroupMembers(apt: Raw): AppointmentGroupMember[] {
  const members = apt.group_members ?? apt.groupMembers
  if (Array.isArray(members)) return members.map(normalizeGroupMember)
  // POST devuelve `group` con la propia cita incluida.
  if (Array.isArray(apt.group)) {
    return apt.group.filter((m) => String(asRaw(m).id) !== String(apt.id)).map(normalizeGroupMember)
  }
  return []
}

/** Tolera `{ appointment, group }` además de la cita plana con `group` adentro. */
export function unwrapGroupedAppointment(nested: unknown): unknown {
  const raw = asRaw(nested)
  if (!raw.appointment || typeof raw.appointment !== 'object') return nested
  const appointment = raw.appointment as Raw
  return { ...appointment, group: raw.group ?? appointment.group }
}

function normalizeReferences(apt: Raw): AppointmentReference[] {
  const list = apt.appointment_references ?? apt.references
  if (!Array.isArray(list)) return []
  return list.map((reference) => {
    // Sin asRaw a propósito: un acompañante null lanza, igual que el JS original (no inventa una fila vacía).
    const r = reference as Raw
    const client = r.client as Raw | null | undefined
    return {
      clientId: (r.client_id ?? client?.id ?? r.clientId ?? null) as AppointmentReference['clientId'],
      name: (client?.full_name ?? r.full_name ?? r.name ?? '') as string,
      phone: (client?.phone ?? r.phone ?? '') as string
    }
  })
}

/** Cita de la API → `Appointment`. Con null/undefined lanza TypeError, como el JS original: getAll no filtra la lista. */
export function normalizeAppointment(input: unknown): Appointment {
  const apt = input as Raw
  const service = apt.service as Raw | null | undefined
  const client = apt.client as Raw | null | undefined
  const worker = apt.worker as Raw | null | undefined
  const serviceOrder = (apt.service_order ?? undefined) as Raw | null | undefined
  const serviceOrderCamel = (apt.serviceOrder ?? undefined) as Raw | null | undefined

  const startTime = ((apt.appointment_time ?? apt.time ?? '') as string).slice(0, 5)
  const duration = Number(apt.duration ?? service?.duration ?? apt.serviceDuration ?? 60)
  const endTime = (apt.end_time ?? apt.endTime ?? calculateEndTime(startTime, duration)) as string

  return {
    id: apt.id as Appointment['id'],
    clientId: (apt.client_id ?? apt.clientId ?? null) as Appointment['clientId'],
    clientName: (apt.client_name ?? apt.clientName ?? client?.full_name ?? 'Sin nombre') as string,
    clientPhone: (apt.client_phone ?? apt.clientPhone ?? client?.phone ?? '') as string,
    clientEmail: (apt.client_email ?? apt.clientEmail ?? client?.email ?? '') as string,
    serviceId: (apt.service_id ?? apt.serviceId ?? null) as Appointment['serviceId'],
    workerId: (apt.worker_id ?? apt.workerId ?? worker?.id ?? null) as Appointment['workerId'],
    workerName: (worker?.full_name ?? apt.worker_name ?? apt.workerName ?? '') as string,
    serviceName: (service?.name ?? apt.serviceName ?? 'Sin servicio') as string,
    date:
      (apt.appointment_date as string | null | undefined)?.split('T')[0] ??
      (apt.date as string | null | undefined)?.split('T')[0] ??
      '',
    time: startTime,
    duration,
    price: (apt.price ?? apt.servicePrice ?? 0) as Appointment['price'],
    servicePrice: (apt.price ?? apt.servicePrice ?? 0) as Appointment['servicePrice'],
    notes: (apt.notes ?? '') as string,
    status: normalizeStatusFromApi(apt),
    endTime,
    createdAt: (apt.created_at ?? apt.createdAt ?? '') as string,
    updatedAt: (apt.updated_at ?? apt.updatedAt ?? '') as string,
    serviceOrderId: (serviceOrder?.id ?? serviceOrderCamel?.id ?? apt.service_order_id ?? apt.serviceOrderId ?? null) as Appointment['serviceOrderId'],
    serviceOrderNumber: (serviceOrder?.queue_number ?? serviceOrderCamel?.queueNumber ?? null) as Appointment['serviceOrderNumber'],
    serviceOrderStatus: (serviceOrder?.status ?? serviceOrderCamel?.status ?? null) as Appointment['serviceOrderStatus'],
    // Estado de COBRO, distinto del status del pipeline: una orden puede estar `in_progress` y `paid` a la vez.
    paymentStatus: (serviceOrder?.payment_status ?? serviceOrderCamel?.paymentStatus ?? apt.payment_status ?? null) as Appointment['paymentStatus'],
    groupId: (apt.group_id ?? apt.groupId ?? null) as Appointment['groupId'],
    groupMembers: normalizeGroupMembers(apt),
    references: normalizeReferences(apt)
  }
}
